<!--
  LowResolutionNotice -- the low-resolution note under a size (card #573).

  Jake, 2026-10-28: three tiers for a raster at the size the distributor
  chose, decided by the SERVER (GET /api/artworks/<id>/resolution or
  `current.resolution` / `candidate.resolution` on GET /api/placements/<id>/sizing):

    * ok            nothing is shown;
    * soft          between the minimum and the recommended DPI: an amber
                    status note, the size is allowed;
    * below_minimum under the minimum: a RED notice carrying the server's
                    sentence (the largest size that passes included). With
                    action "warn" (the default since 2026-10-29) it is only a
                    status ("low resolution ... you can still use it"); with
                    "reject" the size is refused (`blocked`) and the notice is
                    an alert.

  Presentational only: this never recomputes the verdict, so every surface
  agrees. A legacy `{dpi, threshold, low}` object (no `severity`) reads as the
  soft tier. It has no buttons (the optional "Send for art cleanup" lives in
  LowResolutionCleanupAction beside it); disabling the confirm control is the
  host's job (it reads `resolution.blocked`).
-->
<template>
    <p
        v-if="tier === 'stop'"
        class="lowres lowres--stop"
        :role="resolution.blocked ? 'alert' : 'status'"
        data-severity="stop"
        data-testid="low-resolution-stop"
    >
        <!-- A refused size gets the stop sign; one that is only warned about
             (the default, Jake 2026-10-29) gets the warning triangle, so a red
             notice never reads as "blocked" when it is not. -->
        <span
            class="material-icons lowres__icon"
            aria-hidden="true"
        >{{ resolution.blocked ? "report" : "warning" }}</span>
        <span>{{ resolution.message }}</span>
    </p>
    <p
        v-else-if="tier === 'warn'"
        class="lowres"
        role="status"
        data-severity="warn"
        data-testid="low-resolution-warning"
    >
        <span
            class="material-icons lowres__icon"
            aria-hidden="true"
        >warning_amber</span>
        <span>
            This image is about {{ resolution.dpi }} DPI at the size you chose
            ({{ resolution.threshold }} recommended). It may print soft. Use a
            larger file or a smaller size.
        </span>
    </p>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
    // The server's verdict, or null/undefined when no check applies (vector
    // file, unknown size, method with no DPI levels).
    resolution: { type: Object, default: null },
});

const tier = computed(() => {
    const r = props.resolution;
    if (!r) return null;
    const severity = r.severity !== undefined ? r.severity : r.low ? "warn" : null;
    if (severity === "stop" && r.message) return "stop";
    return severity ? "warn" : null;
});
</script>

<style scoped>
.lowres {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 6px 0 0;
    padding: 6px 8px;
    font-size: 12px;
    line-height: 1.4;
    border: 1px solid var(--tf-warning, #fcb900);
    border-radius: 4px;
    background: var(--tf-warning-soft, #fff8e1);
    color: var(--tf-warning-dark, #8a6d3b);
}
.lowres--stop {
    border-color: var(--tf-danger, #b3261e);
    background: var(--tf-danger-soft, #fdecea);
    color: var(--tf-danger-dark, #7a1a14);
}
.lowres__icon {
    font-size: 16px;
    flex: none;
}
</style>
